/* eslint-disable */
import PropTypes from 'prop-types';
import { useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Card, CardHeader, Collapse, Divider, Stack, Typography } from '@mui/material';

// project import
import { ThemeMode } from 'config';
import Highlighter from 'components/third-party/Highlighter';

// header style
const headerSX = {
	p: '10px',
	mb: 3,
	cursor: 'pointer',
	'& .MuiCardHeader-action': {
		m: '0px auto',
		alignSelf: 'center',
	},
};

const CardAccordion = ({
	border = true,
	boxShadow,
	children,
	subheader,
	content = true,
	contentSX = {},
	darkTitle,
	divider = true,
	elevation,
	secondary,
	shadow,
	sx = {},
	title,
	codeHighlight = false,
	codeString,
	modal = false,
	customHeaderSX = {},
	typographySX = {},
	expand = true,

	...others
}) => {
	const theme = useTheme();
	const [expanded, setExpanded] = useState(expand);

	const handleToggle = () => setExpanded((prev) => !prev);

	// Decide whether the boxShadow is on for dark mode
	boxShadow = theme.palette.mode === ThemeMode.DARK ? boxShadow || true : boxShadow;

	return (
		<Card
			elevation={elevation || 0}
			{...others}
			sx={{
				position: 'relative',
				// border: border ? '1px solid' : 'none',
				borderRadius: 0,
				borderColor: theme.palette.mode === ThemeMode.DARK ? theme.palette.divider : theme.palette.grey.A800,
				boxShadow: boxShadow && (!border || theme.palette.mode === ThemeMode.DARK) ? shadow || theme.customShadows.z1 : 'inherit',
				':hover': {
					boxShadow: boxShadow ? shadow || theme.customShadows.z1 : 'inherit',
				},
				...(codeHighlight && {
					'& pre': {
						m: 0,
						p: '12px !important',
						fontFamily: theme.typography.fontFamily,
						fontSize: '0.75rem',
					},
				}),
				...(modal && {
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: { xs: `calc(100% - 50px)`, sm: 'auto' },
					'& .MuiCardContent-root': {
						overflowY: 'auto',
						minHeight: 'auto',
						maxHeight: `calc(100vh - 200px)`,
					},
				}),
				...sx,
			}}
		>
			{/* card header (accordion toggle) */}
			{!darkTitle && title && (
				<CardHeader
					// onClick={handleToggle}
					sx={{ ...headerSX, ...customHeaderSX }}
					titleTypographyProps={{ variant: 'subtitle1' }}
					title={
						<Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
							<Typography variant={darkTitle ? 'h4' : 'subtitle1'} sx={{ ...typographySX }}>
								{title}
							</Typography>
						</Stack>
					}
					action={secondary}
					subheader={subheader}
				/>
			)}

			{darkTitle && title && (
				<CardHeader
					sx={{ ...headerSX, ...customHeaderSX }}
					title={
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							width="100%"
							sx={{
								backgroundColor: '#E1F0FB',
							}}
						>
							<Typography variant={darkTitle ? 'h4' : 'subtitle1'} sx={{ ...typographySX }}>
								{title}
							</Typography>
						</Stack>
					}
					action={secondary}
				/>
			)}

			<Collapse in={expanded} timeout={300 /* 700ms for a slower animation */}>
				{content && children}

				{codeString && (
					<>
						<Divider sx={{ borderStyle: 'dashed' }} />
						<Highlighter codeString={codeString} codeHighlight={codeHighlight} />
					</>
				)}
			</Collapse>
		</Card>
	);
};

CardAccordion.displayName = 'CardAccordion';

CardAccordion.propTypes = {
	border: PropTypes.bool,
	boxShadow: PropTypes.bool,
	children: PropTypes.node,
	subheader: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
	content: PropTypes.bool,
	contentSX: PropTypes.object,
	darkTitle: PropTypes.bool,
	divider: PropTypes.bool,
	elevation: PropTypes.number,
	secondary: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.object]),
	shadow: PropTypes.string,
	sx: PropTypes.object,
	title: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.object]),
	modal: PropTypes.bool,
	codeHighlight: PropTypes.bool,
	codeString: PropTypes.string,
};

export default CardAccordion;
